import { ImgID } from "../../../image_loader/img_id.js";
import { Vector2 } from "../../../common/vector2.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import {
  MyTextAlign,
  MyTextVerticalAlign,
  UITextElement,
} from "../../common_ui_elements/ui_text_element.js";
import type { OfferRewardType } from "../../../api/dto/offers.dto.js";
import { RewardTooltip } from "../../tooltip/reward_tooltip.js";
import { AnchoredTooltip } from "../../tooltip/anchored_tooltip.js";
import { LocalizationManager } from "../../../localization/localization_manager.js";
import { Easing } from "../../../transforms/transform_base.js";
import { TransformRotate } from "../../../transforms/transform_rotate.js";
import { audioController } from "../../../audio/audio_controller.js";
import { AudioClips } from "../../../audio/audio_constants.js";

export enum DailyTaskState
{
  Progress,
  Completed,
  Claimed
}

export type DailyTaskReward =
  | "firework"
  | "infinite_balls"
  | "coins"
  | "heart"
  | "infinite_hearts"
  | "no_ads"
  | "multiballs"
  | "big_balls"
  | "mines";

const REWARD_TOOLTIP_TYPE: Record<Exclude<DailyTaskReward, "mines">, OfferRewardType> = {
  firework: "fireworks",
  infinite_balls: "infinite_balls",
  coins: "coins",
  heart: "hearts",
  infinite_hearts: "infinite_hearts_minutes",
  no_ads: "no_ads_minutes",
  multiballs: "multi_balls",
  big_balls: "big_balls",
};

export interface DailyTaskCardConfig
{
  task_id: string;
  title_text: string;
  state: DailyTaskState;
  progress: number;
  target: number;
  stars: number;
  reward: DailyTaskReward;
  reward_text: string;
  metric?: string;
  metric_param?: string | null;
  /** Task of a day that has not started yet: progress counts, the reward opens on this day. */
  locked_until_day?: number | null;
}

export function taskMetricIconId( metric?: string, param?: string | null ): ImgID
{
  switch ( metric )
  {
    case "login_days": return ImgID.UI_DailyTaskMetricLoginDays;
    case "complete_levels":
      if ( param === "Hard" ) return ImgID.UI_DailyTaskMetricLevelsHard;
      if ( param === "SuperHard" ) return ImgID.UI_DailyTaskMetricLevelsSuperHard;
      return ImgID.UI_DailyTaskMetricLevels;
    case "tower_floors": return ImgID.UI_DailyTaskMetricTowerFloors;
    case "level_streak": return ImgID.UI_DailyTaskMetricLevelStreak;
    case "collect_stars": return ImgID.UI_DailyTaskMetricStars;
    case "collect_coins":
    case "earn_coins":
    case "spend_coins": return ImgID.UI_DailyTaskMetricCoins;
    case "use_boosters": return ImgID.UI_DailyTaskMetricBoosters;
    case "play_attempts": return ImgID.UI_DailyTaskMetricAttempts;
    case "watch_rewarded_ads": return ImgID.UI_DailyTaskMetricAds;
    case "make_purchases": return ImgID.UI_DailyTaskMetricPurchases;
    case "destroy_blocks":
    case "destroy_materials": return ImgID.UI_DailyTaskMetricBlocks;
    case "complete_boss_levels": return ImgID.UI_DailyTaskMetricBoss;
    case "complete_boss_stages": return ImgID.UI_DailyTaskMetricBossStages;
    default: return ImgID.UI_DailyTasksTaskCannon;
  }
}

export class DailyTaskCard extends UIElement
{
  private static readonly TOOLTIP_HOLD_MS = 500;
  private static readonly LOCK_TOOLTIP_OWNER = "daily_task_lock";
  // Padlock wobble on a tap at a locked reward: alternating swings that die out.
  private static readonly LOCK_SHAKE_ANGLES = [ 0.38, -0.32, 0.24, -0.16, 0.08, 0 ];
  private static readonly LOCK_SHAKE_STEP_MS = 65;

  private readonly config: DailyTaskCardConfig;
  private tooltip_icons: { icon: UIImage; get_name: () => string }[] = [];
  private tooltip_hold_timer: number | null = null;
  private readonly on_claim?: ( task_id: string ) => Promise<boolean>;
  private readonly on_state_changed?: ( state: DailyTaskState ) => void;
  private claim_in_flight = false;
  private reward_icon: UIImage | null = null;
  private star_icon: UIImage | null = null;
  private lock_icon: UIImage | null = null;
  private lock_shaking = false;

  public constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    config: DailyTaskCardConfig,
    on_claim?: ( task_id: string ) => Promise<boolean>,
    on_state_changed?: ( state: DailyTaskState ) => void
  )
  {
    super( ctx, x, y, width, height, false, true );
    this.config = { ...config };
    this.on_claim = on_claim;
    this.on_state_changed = on_state_changed;
    this.setOnClick( () => this.handleClick() );
    // Progress is tracked by the server; the only interaction is claiming.
    this.setClickable( this.config.state === DailyTaskState.Completed );
    this.createElements();
  }

  private handleClick(): void
  {
    if ( this.config.state !== DailyTaskState.Completed ) return;
    if ( this.isLocked() )
    {
      this.showLockedFeedback();
      return;
    }
    if ( this.claim_in_flight || !this.on_claim ) return;

    this.claim_in_flight = true;
    void this.on_claim( this.config.task_id ).then( ok =>
    {
      this.claim_in_flight = false;
      if ( !ok ) return;

      this.config.state = DailyTaskState.Claimed;
      this.setClickable( false );
      this.removeAllChildren();
      this.createElements();
      this.forceMakeShouldBeRendered();
      this.on_state_changed?.( this.config.state );
    } );
  }

  // A tap claims a completed task, so there the reward names open on a long
  // press. Otherwise the card is inert and a tap on a reward names it.
  public override onTouchStart( p: Vector2, now: number ): boolean
  {
    const local = this.toLocal( p );
    if ( this.config.state !== DailyTaskState.Completed )
      return this.tooltip_icons.some( ( { icon } ) => icon.onTouchStart( local, now ) )
        || ( this.lock_icon?.onTouchStart( local, now ) ?? false );

    if ( !super.onTouchStart( p, now ) )
      return false;

    this.startTooltipHold( local );
    return true;
  }

  public override onTouchEnd( p: Vector2, now: number, p_global: Vector2 | null = null ): void
  {
    this.stopTooltipHold();
    super.onTouchEnd( p, now, p_global );
  }

  public override cancelTouch( now: number ): void
  {
    this.stopTooltipHold();
    super.cancelTouch( now );
  }

  private startTooltipHold( local: Vector2 ): void
  {
    this.stopTooltipHold();
    const card = this.getAbsoluteRect();
    const x = card.x + local.x;
    const y = card.y + local.y;
    const target = this.tooltip_icons.find( ( { icon } ) =>
    {
      const r = icon.getAbsoluteRect();
      return x >= r.x && x <= r.x + r.width && y >= r.y && y <= r.y + r.height;
    } );
    if ( !target ) return;

    this.tooltip_hold_timer = window.setTimeout( () =>
    {
      this.tooltip_hold_timer = null;
      // Drops the press, so releasing the finger does not claim the task.
      this.cancelTouch( performance.now() );
      RewardTooltip.show( target.icon, target.get_name() );
    }, DailyTaskCard.TOOLTIP_HOLD_MS );
  }

  private stopTooltipHold(): void
  {
    if ( this.tooltip_hold_timer === null ) return;
    window.clearTimeout( this.tooltip_hold_timer );
    this.tooltip_hold_timer = null;
  }

  public getState(): DailyTaskState
  {
    return this.config.state;
  }

  public static hideLockedTooltip(): void
  {
    AnchoredTooltip.hide( DailyTaskCard.LOCK_TOOLTIP_OWNER );
  }

  private isLocked(): boolean
  {
    return ( this.config.locked_until_day ?? 0 ) > 0;
  }

  /** Tap on a reward that opens on a later day: the padlock wobbles and says which day. */
  private showLockedFeedback(): void
  {
    void audioController.playSound( AudioClips.UI_CLICK );
    this.shakeLock();
    const anchor = this.lock_icon ?? this;
    AnchoredTooltip.show( anchor, {
      text: LocalizationManager.tWithParams(
        "DAILY_TASKS.REWARD_LOCKED_UNTIL_DAY",
        [ String( this.config.locked_until_day ) ]
      ),
      anchor_touch_closes: true,
    }, DailyTaskCard.LOCK_TOOLTIP_OWNER );
  }

  private shakeLock(): void
  {
    const lock = this.lock_icon;
    if ( !lock || this.lock_shaking ) return;
    this.lock_shaking = true;

    const step = ( index: number ): void =>
    {
      const angles = DailyTaskCard.LOCK_SHAKE_ANGLES;
      if ( index >= angles.length || this.lock_icon !== lock )
      {
        this.lock_shaking = false;
        return;
      }
      const swing = new TransformRotate(
        lock, true, null, angles[index], performance.now(), DailyTaskCard.LOCK_SHAKE_STEP_MS,
        { easing: Easing.EaseInOutQuad }
      );
      swing.setOnFinished( () => step( index + 1 ) );
      lock.addTransform( swing );
    };
    step( 0 );
  }

  public getConfig(): DailyTaskCardConfig
  {
    return { ...this.config };
  }

  public getRewardAnchor(): { center: Vector2; size: number } | null
  {
    return DailyTaskCard.anchorOf( this.reward_icon );
  }

  public getStarAnchor(): { center: Vector2; size: number } | null
  {
    return DailyTaskCard.anchorOf( this.star_icon );
  }

  private static anchorOf( icon: UIImage | null ): { center: Vector2; size: number } | null
  {
    if ( !icon ) return null;
    const pos = icon.getAbsolutePosition();
    const rect = icon.getRect();
    return { center: new Vector2( pos.x + rect.getW() / 2, pos.y + rect.getH() / 2 ), size: rect.getW() };
  }

  private createElements(): void
  {
    this.createBackground();
    this.createLeftIcon();
    this.createTaskText();
    this.createProgress();
    this.createReward();
    this.createLock();
  }

  private createLock(): void
  {
    this.lock_icon = null;
    this.lock_shaking = false;
    if ( !this.isLocked() || this.config.state === DailyTaskState.Claimed ) return;

    // Same padlock as under the locked day tabs (78x93). It overhangs the
    // card's top-right corner; the tasks viewport leaves room for it.
    const lock_w = 70;
    const lock = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( 940 + 2 - lock_w ),
      LayoutScaler.yFromDesign( -2 ),
      LayoutScaler.widthFromDesign( lock_w ),
      LayoutScaler.heightFromDesign( lock_w * 93 / 78 ),
      ImgID.UI_DailyTasksDayLock
    );
    lock.enableRotation();
    this.addChild( lock );
    this.lock_icon = lock;
    // On a completed card the whole card takes the tap (it is the CLAIM button).
    if ( this.config.state === DailyTaskState.Completed )
      lock.setClickable( false );
    else
      lock.setOnClick( () => this.showLockedFeedback() );
  }

  private createBackground(): void
  {
    const image_id = this.config.state === DailyTaskState.Completed
      ? ImgID.UI_DailyTasksTaskClaim
      : this.config.state === DailyTaskState.Claimed
        ? ImgID.UI_DailyTasksTaskComplete
        : ImgID.UI_DailyTasksTaskNormal;

    const background = new UIImage(
      this.ctx,
      0,
      0,
      this.rect.getW(),
      this.rect.getH(),
      image_id
    );
    this.addVisual( background );
  }

  private createLeftIcon(): void
  {
    const is_claimed = this.config.state === DailyTaskState.Claimed;
    const icon = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( is_claimed ? 43 : 29 ),
      LayoutScaler.yFromDesign( is_claimed ? 39 : 20 ),
      LayoutScaler.widthFromDesign( is_claimed ? 100 : 130 ),
      LayoutScaler.heightFromDesign( is_claimed ? 92 : 130 ),
      is_claimed
        ? ImgID.UI_DailyTasksProgressConfirm
        : taskMetricIconId( this.config.metric, this.config.metric_param )
    );
    this.addVisual( icon );
  }

  private createTaskText(): void
  {
    const is_completed = this.config.state === DailyTaskState.Completed;
    const is_claimed = this.config.state === DailyTaskState.Claimed;
    const title_y = is_completed ? 21.25 : is_claimed ? 42 : 21.36;
    const title_height = is_claimed ? 100 : 54;
    const title_font_size = is_claimed ? 36 : 40;
    const title_text = is_claimed
      ? this.config.title_text.replace( /\s+([+-]?\d[\d.,]*)\s*$/, "\u00A0$1" )
      : this.config.title_text;

    this.addText(
      title_text,
      180.55,
      title_y,
      446,
      title_height,
      title_font_size,
      is_completed ? "#FFFFFF" : "#5E3D27",
      is_completed ? "#398421" : "#FFF4DF",
      5,
      MyTextAlign.Left,
      undefined,
      24,
      is_claimed
    );

    if ( !is_completed ) return;

    this.addText(
      "",
      180.55,
      69.25,
      248,
      84,
      60,
      "#FFFFFF",
      "#28751B",
      12,
      MyTextAlign.Left,
      "CLAIM"
    );
  }

  private createProgress(): void
  {
    if ( this.config.state !== DailyTaskState.Progress ) return;

    const progress = this.config.target > 0
      ? Math.max( 0, Math.min( 1, this.config.progress / this.config.target ) )
      : 0;
    const track_width = 446;
    const track_horizontal_padding = 6;
    const fill_width = ( track_width - track_horizontal_padding * 2 ) * progress;

    if ( progress > 0 )
    {
      const progress_fill = new UINineSliceElement(
        this.ctx,
        LayoutScaler.xFromDesign( 186.55 ),
        LayoutScaler.yFromDesign( 90 ),
        LayoutScaler.widthFromDesign( fill_width ),
        LayoutScaler.heightFromDesign( 40 ),
        ImgID.UI_DailyTasksTaskProgressBar,
        "task_progress_bar",
        undefined,
        undefined,
        false,
        true
      );
      this.addVisual( progress_fill );
    }

    this.addText(
      `${this.config.progress}/${this.config.target}`,
      180.55,
      84,
      446,
      50,
      30,
      "#FFFFFF",
      "#0F625E",
      5,
      MyTextAlign.Center
    );
  }

  private createReward(): void
  {
    const content_offset_x = -8;
    this.tooltip_icons = [];
    const dimmed = this.config.state === DailyTaskState.Claimed;
    const image_options = dimmed ? { darkness_factor: 0.48 } : undefined;
    const text_color = dimmed ? "#72A9B8" : "#FFFFFF";
    const text_outline = dimmed ? "#126B80" : "#07529B";

    const star = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( 658 + content_offset_x ),
      LayoutScaler.yFromDesign( 34 ),
      LayoutScaler.widthFromDesign( 100 ),
      LayoutScaler.heightFromDesign( 100 ),
      ImgID.UI_DailyTasksProgressStar,
      image_options
    );
    this.addTooltipIcon( star, () => RewardTooltip.starsName() );
    this.star_icon = star;

    this.addText(
      String( this.config.stars ), 687 + content_offset_x, 95.1, 48, 53, 38,
      text_color, text_outline, 9, MyTextAlign.Center
    );

    const plus_width = 55;
    const star_visible_right = 658 + content_offset_x + 152 / 157 * 100;
    const reward_visible_left = this.getRewardVisibleLeft() + content_offset_x;
    const plus_center_x = ( star_visible_right + reward_visible_left ) / 2;
    this.addText(
      "+", plus_center_x - plus_width / 2, 66, plus_width, 55, 42,
      text_color, text_outline, 9, MyTextAlign.Center
    );

    const reward = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( 807 + content_offset_x ),
      LayoutScaler.yFromDesign( 34 ),
      LayoutScaler.widthFromDesign( 100 ),
      LayoutScaler.heightFromDesign( 100 ),
      this.getRewardImageId(),
      image_options
    );
    const config_reward = this.config.reward;
    this.addTooltipIcon( reward, () => config_reward === "mines"
      ? RewardTooltip.nameOfLegacy( "mines" )
      : RewardTooltip.nameOf( REWARD_TOOLTIP_TYPE[config_reward] ) );
    this.reward_icon = reward;

    this.addText(
      this.config.reward_text, 812 + content_offset_x, 95.1, 90, 53, 38,
      text_color, text_outline, 9, MyTextAlign.Center
    );
  }

  private getRewardImageId(): ImgID
  {
    switch ( this.config.reward )
    {
      case "coins": return ImgID.UI_DailyTasksTaskCoins;
      case "heart": return ImgID.IconHeart;
      case "infinite_hearts": return ImgID.UI_ShopInfiniteHeart;
      case "no_ads": return ImgID.UI_RemoveAdsIcon;
      case "infinite_balls": return ImgID.UI_GameStartInfiniteBallsBooster;
      case "multiballs": return ImgID.UI_GameStartMultiballsBooster;
      case "big_balls": return ImgID.UI_GameStartBigBallBooster;
      case "mines": return ImgID.UI_PassiveBoosterBomb;
      default: return ImgID.UI_GameStartFireworkBooster;
    }
  }

  private getRewardVisibleLeft(): number
  {
    if ( this.config.reward === "coins" ) return 807 + 2;
    if ( this.config.reward === "heart" ) return 807 + 2;
    if ( this.config.reward === "infinite_hearts" ) return 807 + 2;
    if ( this.config.reward === "no_ads" ) return 807 + 2;
    if ( this.config.reward === "infinite_balls" ) return 807 + 51 / 256 * 100;
    return 807 + 39 / 256 * 100;
  }

  private addText(
    text: string,
    x: number,
    y: number,
    width: number,
    height: number,
    font_size: number,
    color: string,
    outline: string,
    outline_width: number,
    text_align: MyTextAlign = MyTextAlign.Left,
    localization_key?: string,
    min_font_size: number = Math.max( 20, font_size - 10 ),
    auto_wrap: boolean = false
  ): void
  {
    const text_element = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( x ),
      LayoutScaler.yFromDesign( y ),
      LayoutScaler.widthFromDesign( width ),
      LayoutScaler.heightFromDesign( height ),
      {
        text,
        localization_key,
        font_size: LayoutScaler.textSizeFromDesign( font_size ),
        min_font_size: LayoutScaler.textSizeFromDesign( min_font_size ),
        font_weight: 900,
        text_color: color,
        text_style: TextStyleScheme.geologica,
        text_align,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( outline_width ),
        outline_color: outline,
        outline_shadow_blur: 0,
        outline_shadow_color: "rgba(0, 0, 0, 0)",
        outline_shadow_offset_x: 0,
        outline_shadow_offset_y: 0,
        shadow_blur: 0,
        shadow_color: "rgba(0, 0, 0, 0)",
        shadow_offset_x: 0,
        shadow_offset_y: 0,
        auto_wrap,
        fit_font_size: true,
      }
    );
    this.addVisual( text_element );
  }

  private addVisual( element: UIElement ): void
  {
    element.setClickable( false );
    this.addChild( element );
  }

  /** Tap-to-name icon; on a completed card it stays click-through so a tap claims (the name opens on a hold). */
  private addTooltipIcon( icon: UIImage, get_name: () => string ): void
  {
    this.addChild( icon );
    this.tooltip_icons.push( { icon, get_name } );
    if ( this.config.state === DailyTaskState.Completed )
      icon.setClickable( false );
    else
      RewardTooltip.attachName( icon, get_name );
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    this.finishRender();
  }

  public name(): string
  {
    return "DailyTaskCard";
  }
}
